import { useState, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  ArrowLeft,
  Share2,
  Eye,
  EyeOff,
  Box,
  Calendar,
  HardDrive,
  Triangle,
  Layers,
  Palette,
  Image,
  Play,
  Ruler,
  Sparkles,
} from 'lucide-react';
import type { UserModel } from '@visionar/shared';
import { useUIStore } from '@/stores/ui-store';
import Button from '@/components/ui/Button';
import ModelPreview3D, { type ModelMetadata } from '@/components/features/models/ModelPreview3D';

interface UserModelDetailProps {
  model: UserModel;
}

// Skeleton placeholder component
function StatSkeleton() {
  return (
    <div className="bg-white/5 rounded-xl p-3 text-center animate-pulse">
      <div className="w-5 h-5 bg-white/10 rounded mx-auto mb-1.5" />
      <div className="w-8 h-5 bg-white/10 rounded mx-auto mb-1" />
      <div className="w-12 h-2 bg-white/5 rounded mx-auto" />
    </div>
  );
}

export default function UserModelDetail({ model }: UserModelDetailProps) {
  const { setSelectedUserModel, setARActive, setARModel } = useUIStore();
  const [metadata, setMetadata] = useState<ModelMetadata | null>(null);

  const handleClose = () => {
    setSelectedUserModel(null);
  };

  const handleViewInAR = () => {
    setARModel(model);
    setARActive(true);
  };

  const handleMetadataLoaded = useCallback((data: ModelMetadata) => {
    setMetadata(data);
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: '100%' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed inset-0 bg-background z-[100] overflow-hidden flex flex-col"
    >
      {/* 3D Model Preview */}
      <div className="relative h-[40%] min-h-[280px] bg-gradient-to-br from-accent-blue/10 via-accent-purple/10 to-background">
        <ModelPreview3D
          modelUrl={model.modelUrl}
          className="w-full h-full"
          onMetadataLoaded={handleMetadataLoaded}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent pointer-events-none" />

        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 p-4 pt-12 flex justify-between items-center safe-area-top">
          <button
            onClick={handleClose}
            className="w-11 h-11 rounded-full glass-dark flex items-center justify-center touch-feedback"
          >
            <ArrowLeft size={22} />
          </button>
          <div className="flex gap-2">
            <button className="w-11 h-11 rounded-full glass-dark flex items-center justify-center touch-feedback">
              <Share2 size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto -mt-6">
        <div className="glass rounded-t-[28px] p-5 min-h-full">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              {/* Visibility Badge */}
              <div
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase mb-2 ${
                  model.isPublic
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-white/10 text-white/50'
                }`}
              >
                {model.isPublic ? <Eye size={11} /> : <EyeOff size={11} />}
                {model.isPublic ? 'Public' : 'Private'}
              </div>
              {/* Name */}
              <h1 className="text-2xl font-bold truncate">{model.name}</h1>
            </div>
            {/* Format Badge */}
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-blue/20 text-accent-blue text-xs font-semibold">
              <Box size={14} />
              GLB
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="flex items-center gap-4 mb-5 text-white/50 text-xs">
            <span className="flex items-center gap-1.5">
              <Eye size={14} />
              {model.viewCount.toLocaleString()} views
            </span>
            <span className="flex items-center gap-1.5">
              <HardDrive size={14} />
              {formatFileSize(model.fileSize)}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} />
              {formatDate(model.createdAt)}
            </span>
          </div>

          {/* Description */}
          {model.description && (
            <div className="mb-5">
              <p className="text-white/70 text-sm leading-relaxed">{model.description}</p>
            </div>
          )}

          {/* Model Stats Grid - Always visible */}
          <div className="mb-5">
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">
              Model Details
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {metadata ? (
                <>
                  {/* Triangles */}
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Triangle size={18} className="mx-auto mb-1.5 text-accent-purple" />
                    <div className="text-lg font-bold">{formatNumber(metadata.triangleCount)}</div>
                    <div className="text-[10px] text-white/40 uppercase">Triangles</div>
                  </div>
                  {/* Vertices */}
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Sparkles size={18} className="mx-auto mb-1.5 text-accent-blue" />
                    <div className="text-lg font-bold">{formatNumber(metadata.vertexCount)}</div>
                    <div className="text-[10px] text-white/40 uppercase">Vertices</div>
                  </div>
                  {/* Meshes */}
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Layers size={18} className="mx-auto mb-1.5 text-green-400" />
                    <div className="text-lg font-bold">{metadata.meshCount}</div>
                    <div className="text-[10px] text-white/40 uppercase">Meshes</div>
                  </div>
                  {/* Materials */}
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Palette size={18} className="mx-auto mb-1.5 text-orange-400" />
                    <div className="text-lg font-bold">{metadata.materialCount}</div>
                    <div className="text-[10px] text-white/40 uppercase">Materials</div>
                  </div>
                  {/* Textures */}
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Image size={18} className="mx-auto mb-1.5 text-pink-400" />
                    <div className="text-lg font-bold">{metadata.textureCount}</div>
                    <div className="text-[10px] text-white/40 uppercase">Textures</div>
                  </div>
                  {/* Animations */}
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <Play size={18} className="mx-auto mb-1.5 text-yellow-400" />
                    <div className="text-lg font-bold">{metadata.animationCount}</div>
                    <div className="text-[10px] text-white/40 uppercase">Animations</div>
                  </div>
                </>
              ) : (
                <>
                  <StatSkeleton />
                  <StatSkeleton />
                  <StatSkeleton />
                  <StatSkeleton />
                  <StatSkeleton />
                  <StatSkeleton />
                </>
              )}
            </div>
          </div>

          {/* Dimensions - Always visible */}
          <div className="mb-5">
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">
              Dimensions
            </h3>
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <Ruler size={18} className="text-accent-blue" />
                <span className="text-sm text-white/70">Bounding Box (units)</span>
              </div>
              {metadata ? (
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-lg font-mono font-bold">{metadata.dimensions.width}</div>
                    <div className="text-[10px] text-white/40">Width</div>
                  </div>
                  <div>
                    <div className="text-lg font-mono font-bold">{metadata.dimensions.height}</div>
                    <div className="text-[10px] text-white/40">Height</div>
                  </div>
                  <div>
                    <div className="text-lg font-mono font-bold">{metadata.dimensions.depth}</div>
                    <div className="text-[10px] text-white/40">Depth</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 text-center animate-pulse">
                  <div>
                    <div className="w-10 h-5 bg-white/10 rounded mx-auto mb-1" />
                    <div className="w-8 h-2 bg-white/5 rounded mx-auto" />
                  </div>
                  <div>
                    <div className="w-10 h-5 bg-white/10 rounded mx-auto mb-1" />
                    <div className="w-8 h-2 bg-white/5 rounded mx-auto" />
                  </div>
                  <div>
                    <div className="w-10 h-5 bg-white/10 rounded mx-auto mb-1" />
                    <div className="w-8 h-2 bg-white/5 rounded mx-auto" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Animations List - Only if has animations */}
          {metadata?.hasAnimations && (
            <div className="mb-5">
              <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">
                Animations
              </h3>
              <div className="flex flex-wrap gap-2">
                {metadata.animationNames.map((name, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-400 text-xs"
                  >
                    <Play size={12} />
                    {name || `Animation ${i + 1}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Features Tags - Always visible */}
          <div className="mb-24">
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">
              Features
            </h3>
            <div className="flex flex-wrap gap-2">
              {metadata ? (
                <>
                  {metadata.hasPBRMaterials && (
                    <span className="px-3 py-1.5 rounded-full bg-accent-purple/20 text-accent-purple text-xs font-medium">
                      PBR Materials
                    </span>
                  )}
                  {metadata.hasAnimations && (
                    <span className="px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-medium">
                      Animated
                    </span>
                  )}
                  {metadata.textureCount > 0 && (
                    <span className="px-3 py-1.5 rounded-full bg-pink-500/20 text-pink-400 text-xs font-medium">
                      Textured
                    </span>
                  )}
                  <span className="px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                    AR Ready
                  </span>
                </>
              ) : (
                <div className="flex gap-2 animate-pulse">
                  <div className="w-20 h-7 bg-white/5 rounded-full" />
                  <div className="w-16 h-7 bg-white/5 rounded-full" />
                  <div className="w-18 h-7 bg-white/5 rounded-full" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="absolute bottom-0 left-0 right-0 p-5 pb-7 safe-area-bottom bg-gradient-to-t from-background via-background to-transparent">
        <Button fullWidth size="lg" onClick={handleViewInAR}>
          View in AR
        </Button>
      </div>
    </motion.div>
  );
}
